/* eslint-disable prefer-template */
import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TextField
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  differenceInMinutes,
  getHours,
  isPast,
  isToday,
  isValid,
  isWeekend,
  parseISO
} from 'date-fns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBRLocale from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useAuth } from '../../../../../../hooks/Auth';
import { useIntegration } from '../../../../../../hooks/Integration';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { hours } from '../../../../../../helpers/hours';
import { SearchMyCollectionsContext } from '../..';
import * as S from './styled';

export const ModalExtendMultiple = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmExtension, setConfirmExtension] = useState(false);
  const [completedExtension, setCompletedExtension] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [fromHours, setFromHours] = useState([]);
  const [untilHours, setUntilHours] = useState([]);
  const [loadAvailableDate, setLoadAvailableDate] = useState([]);
  const [loadAvailableFrom, setLoadAvailableFrom] = useState([]);
  const [loadAvailableUntil, setLoadAvailableUntil] = useState([]);
  const [extendReason, setExtendReason] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [isDisabledButton, setIsDisabledButton] = useState(true);
  const [protocolTextAll, setProtocolTextAll] = useState({});

  const { token } = useAuth();
  const { getHolidaysByZipPostalCode } = useIntegration();
  const { closeModal, Modal } = useModal();
  const { extendPickupDate } = usePickup();

  const {
    extensionReasonList,
    setExtensionReasonList,
    openOtherExtensionToFill,
    setOpenOtherExtensionToFill,
    invoiceListToModal,
    dataToExtend,
    setDataToExtend,
    zipToModal,
    pickupIdToModal,
    protocolToModal,
    branchParameterizationToModal,
    refreshSearch,
    protocolExtend,
    setProtocolExtend,
    enablePickupOnWeekends,
    hourLimitOnWeekends,
    enablePickupOnHolidays,
    hourLimitOnHolidays,
    enableSlotTimeToPickupSchedule,
    hourSlotTimeToPickupSchedule,
    minuteSlotTimeToPickupSchedule
  } = useContext(SearchMyCollectionsContext);

  const maxSM = useMediaQuery('(max-width:768px)');

  const extendPickupSchema = Yup.object().shape({});

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(extendPickupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = async () => {
    let hasError = false;
    let hasTimeError = false;
    let hasInvalidDate = false;
    let hasHolidayError = false;

    extendReason.forEach((value) => {
      if (
        !value.id ||
        (extensionReasonList?.find((element) => element.cancelId === value.id)
          ?.cancelDescription === 'OUTROS MOTIVOS' &&
          !value?.otherReason)
      ) {
        hasError = true;
      }
    });

    loadAvailableDate.forEach((value) => {
      if (value === '') hasError = true;
    });

    loadAvailableDate.forEach((value) => {
      if (isPast(new Date(value)) || isToday(new Date(value))) {
        hasInvalidDate = true;
      }
    });

    if (!enablePickupOnWeekends) {
      loadAvailableDate.forEach((value) => {
        if (isWeekend(new Date(value))) {
          hasHolidayError = true;
        }
      });
    }

    if (!enablePickupOnHolidays) {
      loadAvailableDate.forEach((value) => {
        if (holidayList?.includes(new Date(value).toDateString())) {
          hasHolidayError = true;
        }
      });
    }

    loadAvailableFrom.forEach((value) => {
      if (value === '') hasError = true;
    });

    loadAvailableUntil.forEach((value) => {
      if (value === '') hasError = true;
    });

    extendReason.forEach((value) => {
      if (value === '') hasError = true;
    });

    invoiceListToModal.forEach((_, index) => {
      const fromDate = new Date(
        2000,
        0,
        1,
        loadAvailableFrom[index].split(':')[0],
        loadAvailableFrom[index].split(':')[1]
      );

      const untilDate = new Date(
        2000,
        0,
        1,
        loadAvailableUntil[index].split(':')[0],
        loadAvailableUntil[index].split(':')[1]
      );

      if (differenceInMinutes(fromDate, untilDate) >= 0) hasTimeError = true;
    });

    if (hasError) {
      toast(
        <Toast
          title="Atenção!"
          content="Preencha todos os campos."
          type="error"
        />,
        options
      );
    } else if (hasInvalidDate) {
      toast(
        <Toast
          title="Atenção!"
          content="A data de coleta deverá ser maior que a data de hoje."
          type="error"
        />,
        options
      );
    } else if (hasHolidayError) {
      toast(
        <Toast
          title="Atenção!"
          content="A data informada para a coleta não está disponível."
          type="error"
        />,
        options
      );
    } else if (hasTimeError) {
      toast(
        <Toast
          title="Atenção!"
          content="Hora final deve ser após hora inicial."
          type="error"
        />,
        options
      );
    } else {
      setConfirmExtension(true);
    }
  };

  const handleExtendPickup = async () => {
    try {
      setIsSubmitting(true);

      let newExtensionList = [];

      invoiceListToModal?.forEach((value, index) => {
        const date = new Date(loadAvailableDate[index])
          ?.toISOString()
          .split('T')[0];

        let [hour] = loadAvailableFrom[index].split(':');
        const dateFrom = String(
          date + 'T' + String(hour).padStart(2, '0') + ':00:00.000'
        );

        [hour] = loadAvailableUntil[index].split(':');
        const dateTo = String(
          date + 'T' + String(hour).padStart(2, '0') + ':00:00.000'
        );

        const extension = {
          taxInvoiceId: value.taxInvoiceId,
          extensionReasonId: extendReason[index]?.id,
          extensionOtherReason: extendReason[index]?.extensionOtherReason || '',
          dateFrom,
          dateTo,
          loadAvailableDate: new Date(loadAvailableDate[index]).toISOString()
        };

        newExtensionList.push(extension);
      });

      const paramsList = [];

      while (newExtensionList.length > 0) {
        const firstItem = newExtensionList[0];

        const sameDate = newExtensionList.filter(
          (value) => value.loadAvailableDate === firstItem.loadAvailableDate
        );

        let mainProtocol = '';

        if (invoiceListToModal?.length > 0) {
          if (invoiceListToModal[0]?.pickupTaxExtensionList?.length > 0) {
            mainProtocol = invoiceListToModal[0]?.pickupTaxExtensionList[0]
              ?.mainProtocol
              ? invoiceListToModal[0]?.pickupTaxExtensionList[0]?.mainProtocol
              : protocolToModal;
          } else {
            mainProtocol = protocolToModal;
          }
        } else {
          mainProtocol = protocolToModal;
        }

        const params = {
          managerParameterizationId: branchParameterizationToModal,
          mainProtocol,
          pickupId: pickupIdToModal,
          informacoesProrrogacao: []
        };

        sameDate.forEach((value) => {
          params.informacoesProrrogacao.push({
            taxInvoiceId: value.taxInvoiceId,
            extensionReasonId: value.extensionReasonId,
            extensionOtherReason: value.extensionOtherReason,
            dateFrom: value.dateFrom,
            dateTo: value.dateTo
          });
        });

        paramsList.push(params);

        newExtensionList = newExtensionList.filter(
          (value) => value.loadAvailableDate !== firstItem.loadAvailableDate
        );
      }

      let hasFailed = false;
      // eslint-disable-next-line no-restricted-syntax
      for await (const params of paramsList) {
        const response = await extendPickupDate({
          data: params,
          token
        });

        if (response.status !== 200) {
          hasFailed = true;
        }
      }

      if (!hasFailed) {
        toast(
          <Toast
            title="Sucesso!"
            content="Notas fiscais prorrogadas com sucesso."
            type="success"
          />,
          options
        );
        setProtocolExtend(protocolTextAll);
        setConfirmExtension(false);
        setCompletedExtension(true);
        refreshSearch();
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setConfirmExtension(false);
      setCompletedExtension(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const disableWeekends = (date) => {
    let isHoliday = false;

    if (holidayList?.length > 0) {
      const newDate = new Date(date).toDateString();

      isHoliday = holidayList?.includes(newDate);
    }

    return (
      (!enablePickupOnWeekends &&
        (date.getDay() === 0 || date.getDay() === 6)) ||
      (!enablePickupOnHolidays && isHoliday)
    );
  };

  useEffect(() => {
    const newUntilHours = loadAvailableDate?.map(() => {
      return hours;
    });
    const newLoadAvailableUntil = [...loadAvailableUntil];

    loadAvailableDate?.forEach((value, index) => {
      const availableFrom = loadAvailableFrom[index];
      const availableUntil = loadAvailableUntil[index];

      if (availableFrom && enablePickupOnWeekends) {
        const fromHour = String(availableFrom)?.split(':')[0];
        let maxHour = 24;

        if (isWeekend(value)) {
          maxHour = hourLimitOnWeekends || 24;

          newUntilHours[index] = newUntilHours[index].filter(({ hour }) => {
            return (
              hour.split(':')[0] > fromHour && hour.split(':')[0] <= maxHour
            );
          });
        }
      }

      if (availableFrom && enablePickupOnHolidays) {
        const fromHour = String(availableFrom)?.split(':')[0];
        let maxHour = 24;

        if (holidayList?.includes(new Date(value).toDateString())) {
          maxHour = hourLimitOnHolidays || 24;

          newUntilHours[index] = newUntilHours[index].filter(({ hour }) => {
            return (
              hour.split(':')[0] > fromHour && hour.split(':')[0] <= maxHour
            );
          });
        }
      }

      if (availableFrom && enableSlotTimeToPickupSchedule) {
        const fromHour = String(availableFrom)?.split(':')[0];
        const interval = hourSlotTimeToPickupSchedule || 1;

        newUntilHours[index] = newUntilHours[index].filter(({ hour }) => {
          return (
            hour.split(':')[0] > fromHour &&
            (hour.split(':')[0] - fromHour) % interval === 0
          );
        });
      }

      const findHour = newUntilHours[index].find(({ hour }) => {
        return hour === availableUntil;
      });

      if (!findHour) {
        newLoadAvailableUntil[index] = '';
      }
    });

    setUntilHours(newUntilHours);
    setLoadAvailableUntil(newLoadAvailableUntil);
  }, [JSON.stringify(loadAvailableDate), JSON.stringify(loadAvailableFrom)]);

  useEffect(() => {
    const newLoadAvailableFrom = [...loadAvailableFrom];
    const newFromHours = [...fromHours];

    invoiceListToModal.forEach((_, index) => {
      if (!loadAvailableDate[index]) {
        return;
      }

      if (isToday(loadAvailableDate[index])) {
        const hour = getHours(new Date());

        const hoursFiltered = hours.filter((value) => {
          return value.hour.split(':')[0] > hour;
        });

        newFromHours[index] = hoursFiltered;

        const findHour = hoursFiltered.findIndex((value) => {
          return value.hour === loadAvailableFrom[index];
        });

        if (findHour < 0) {
          newLoadAvailableFrom[index] = '';
        }
      } else {
        newFromHours[index] = hours;
      }
    });

    setLoadAvailableFrom(newLoadAvailableFrom);
    setFromHours(newFromHours);
  }, [JSON.stringify(loadAvailableDate)]);

  useEffect(() => {
    let allLoadAvailableDateFilled = true;
    let allLoadAvailableFromFilled = true;
    let allLoadAvailableUntilFilled = true;
    let allExtendReasonFilled = true;
    let allExtensionOtherReason = true;

    if (loadAvailableDate?.some((value) => !value)) {
      allLoadAvailableDateFilled = false;
    }

    if (loadAvailableFrom?.some((value) => !value)) {
      allLoadAvailableFromFilled = false;
    }

    if (loadAvailableUntil?.some((value) => !value)) {
      allLoadAvailableUntilFilled = false;
    }

    if (extendReason?.some((value) => !value?.id)) {
      allExtendReasonFilled = false;
    }

    if (
      openOtherExtensionToFill &&
      extendReason?.some(
        (value) =>
          !value?.extensionOtherReason &&
          extensionReasonList?.find(
            (element) => element.extensionId === value.id
          )?.extensionDescription === 'OUTROS MOTIVOS'
      )
    ) {
      allExtensionOtherReason = false;
    }

    if (
      allLoadAvailableDateFilled &&
      allLoadAvailableFromFilled &&
      allLoadAvailableUntilFilled &&
      allExtendReasonFilled &&
      allExtensionOtherReason
    ) {
      setIsDisabledButton(false);
    } else {
      setIsDisabledButton(true);
    }
  }, [
    JSON.stringify(loadAvailableDate),
    JSON.stringify(loadAvailableFrom),
    JSON.stringify(loadAvailableUntil),
    JSON.stringify(extendReason)
  ]);

  useEffect(async () => {
    setProtocolTextAll(protocolToModal);
    const newLoadAvailableFrom = [];
    const newLoadAvailableDate = [];
    const newLoadAvailableUntil = [];
    const newExtendReason = [];
    const newHours = [];

    invoiceListToModal.forEach(() => {
      newLoadAvailableFrom.push('');
      newLoadAvailableDate.push('');
      newLoadAvailableUntil.push('');
      newExtendReason.push('');
      newHours.push(hours);
    });

    setLoadAvailableFrom(newLoadAvailableFrom);
    setLoadAvailableDate(newLoadAvailableDate);
    setLoadAvailableUntil(newLoadAvailableUntil);
    setExtendReason(newExtendReason);
    setFromHours(newHours);
    setUntilHours(newHours);

    if (zipToModal) {
      try {
        const holidaysResponse = await getHolidaysByZipPostalCode({
          zipPostalCode: zipToModal,
          token
        });

        if (holidaysResponse?.status === 200) {
          const holidays = holidaysResponse?.data?.data?.list.map((holiday) => {
            const date = new Date(holiday.date).toDateString();
            return date;
          });

          setHolidayList(holidays);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    }
  }, []);

  return (
    <Modal preventClose>
      {!confirmExtension && !completedExtension && !confirmClose && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              maxHeight: '86vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <Stack spacing={2} style={{ padding: '8px' }}>
              <S.ModalTitle>Prorrogação da Coleta</S.ModalTitle>
              <S.SectionTitle>Selecione a nova data da coleta:</S.SectionTitle>
              {invoiceListToModal?.map((value, index) => (
                <Stack key={value.taxInvoiceId} spacing={2} direction="column">
                  <S.SectionTitle style={{ textAlign: 'left' }}>
                    NF: {value?.number}
                  </S.SectionTitle>

                  <Stack direction={maxSM ? 'column' : 'row'} spacing={2}>
                    <Controller
                      name="loadAvailableDate"
                      defaultValue=""
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          locale={ptBRLocale}
                        >
                          <DatePicker
                            label="Data"
                            mask="__/__/____"
                            {...field}
                            value={loadAvailableDate[index]}
                            onChange={(date) => {
                              const newLoadAvailableDate = [
                                ...loadAvailableDate
                              ];

                              newLoadAvailableDate[index] = date;

                              setLoadAvailableDate(newLoadAvailableDate);
                            }}
                            shouldDisableDate={disableWeekends}
                            minDate={new Date()}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                onBlur={(event) => {
                                  if (event?.target?.value?.length === 8) {
                                    const [day, month, year] =
                                      event.target.value.split('/');

                                    const newDate = new Date(
                                      String('20' + year),
                                      month - 1,
                                      day
                                    );

                                    setLoadAvailableDate(newDate);
                                    setValue('loadAvailableDate', newDate);
                                  }
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  placeholder: 'DD/MM/AAAA'
                                }}
                                style={{ width: maxSM ? '100%' : 192 }}
                                error={!!errors.loadAvailableDate}
                                helperText={errors.loadAvailableDate?.message}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )}
                    />
                    <Controller
                      name="loadAvailableFrom"
                      defaultValue=""
                      control={control}
                      render={({ field }) => (
                        <FormControl style={{ width: maxSM ? '100%' : 128 }}>
                          <InputLabel id="loadAvailableFrom">
                            A partir de
                          </InputLabel>
                          <Select
                            id="loadAvailableFrom"
                            label="A partir de"
                            {...field}
                            value={loadAvailableFrom[index]}
                            onChange={(event) => {
                              const newLoadAvailableFrom = [
                                ...loadAvailableFrom
                              ];

                              newLoadAvailableFrom[index] = event.target.value;

                              setLoadAvailableFrom(newLoadAvailableFrom);
                            }}
                          >
                            {fromHours[index].map(({ id, hour }) => (
                              <MenuItem key={id} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="loadAvailableUntil"
                      defaultValue=""
                      control={control}
                      render={({ field }) => (
                        <FormControl style={{ width: maxSM ? '100%' : 128 }}>
                          <InputLabel id="loadAvailableUntil">Até</InputLabel>
                          <Select
                            id="loadAvailableUntil"
                            label="Até"
                            {...field}
                            value={loadAvailableUntil[index]}
                            onChange={(event) => {
                              const newLoadAvailableUntil = [
                                ...loadAvailableUntil
                              ];

                              newLoadAvailableUntil[index] = event.target.value;

                              setLoadAvailableUntil(newLoadAvailableUntil);
                            }}
                          >
                            {untilHours[index].map(({ id, hour }) => (
                              <MenuItem key={id} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Stack>

                  <Controller
                    name="extendReason"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        error={!!errors.extendReason}
                        style={{ width: maxSM ? 360 : '100%' }}
                      >
                        <InputLabel id="extendReason">
                          Selecione o motivo da prorrogação:
                        </InputLabel>
                        <Select
                          id="extendReason"
                          label="Selecione o motivo da prorrogação:"
                          {...field}
                          value={extendReason[index]?.id}
                          onChange={(event) => {
                            const newExtendReason = [...extendReason];

                            newExtendReason[index] = {
                              id: event.target.value
                            };

                            setExtendReason(newExtendReason);
                          }}
                        >
                          {extensionReasonList.map(
                            ({ extensionDescription, extensionId }) => (
                              <MenuItem key={extensionId} value={extensionId}>
                                {extensionDescription}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    )}
                  />

                  {extensionReasonList?.find(
                    (element) => element.extensionId === extendReason[index]?.id
                  )?.extensionDescription === 'OUTROS MOTIVOS' &&
                    openOtherExtensionToFill && (
                      <Controller
                        name="otherExtendReason"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                          <TextField
                            name="otherExtendReason"
                            id="otherExtendReason"
                            label="Motivo"
                            {...field}
                            value={extendReason[index]?.extensionOtherReason}
                            onChange={(e) => {
                              const newExtendOtherReason = [...extendReason];

                              newExtendOtherReason[index] = {
                                ...newExtendOtherReason[index],
                                extensionOtherReason: e.target.value
                              };
                              setExtendReason(newExtendOtherReason);
                              field.onChange(e.target.value);
                            }}
                            style={{ width: '100%' }}
                            error={!!errors.otherExtendReason}
                            helperText={errors.otherExtendReason?.message}
                          />
                        )}
                      />
                      // eslint-disable-next-line prettier/prettier
                  )}
                </Stack>
              ))}

              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 32
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setConfirmClose(true);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isDisabledButton}
                >
                  Confirmar
                </Button>
              </Box>
            </Stack>
          </Box>
        </form>
      )}

      {confirmExtension && (
        <Stack spacing={2}>
          <S.ModalTitle>Prorrogar Coleta</S.ModalTitle>
          <S.ModalText>
            <>
              <S.Text>
                {invoiceListToModal.length === 1 &&
                  invoiceListToModal[0].number === 0 && (
                    <>
                      <S.Text>
                        A Nota Fiscal do Protocolo {protocolToModal} será
                        prorrogada.
                      </S.Text>
                    </>
                    // eslint-disable-next-line indent
                  )}
              </S.Text>
              <S.Text>
                {invoiceListToModal.length === 1 &&
                  invoiceListToModal[0].number !== 0 && (
                    <>
                      A Nota Fiscal{' '}
                      {invoiceListToModal?.map(({ number }, index) => number)}{' '}
                      do Protocolo {protocolToModal} será prorrogada.
                    </>
                    // eslint-disable-next-line indent
                  )}
              </S.Text>
              <S.Text>
                {invoiceListToModal.length > 1 && (
                  <>
                    As Notas Fiscais{' '}
                    {invoiceListToModal?.map(
                      ({ number, taxInvoiceId }, index) => {
                        return (
                          <S.Text key={taxInvoiceId}>
                            {number}
                            {/* eslint-disable-next-line prettier/prettier */}
                            {invoiceListToModal.length - 1 === index ? '' : ', '}
                          </S.Text>
                        );
                      }
                    )}{' '}
                    do Protocolo {protocolToModal} serão prorrogadas.
                  </>
                )}
              </S.Text>
            </>
          </S.ModalText>
          <S.ModalText>Deseja Continuar?</S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => {
                setOpenOtherExtensionToFill(true);
                setExtensionReasonList([]);
                setDataToExtend({});
                closeModal();
              }}
              disabled={isSubmitting}
            >
              Não
            </Button>
            <LoadingButton
              type="button"
              variant="contained"
              onClick={() => {
                handleExtendPickup();
              }}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Sim
            </LoadingButton>
          </Box>
        </Stack>
      )}

      {completedExtension && (
        <Stack spacing={2}>
          <S.ModalTitle>Prorrogação Concluído</S.ModalTitle>
          <S.ModalText>
            Sua solicitação para prorrogação da coleta de Protocolo{' '}
            {protocolToModal} foi realizada com sucesso.
            <br />
            <br />
            Em breve receberá um e-mail!
          </S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                setOpenOtherExtensionToFill(true);
                setExtensionReasonList([]);
                setDataToExtend({});
                closeModal();
              }}
            >
              Ok
            </Button>
          </Box>
        </Stack>
      )}

      {confirmClose && (
        <Stack spacing={2}>
          <S.ModalTitle>Atenção</S.ModalTitle>

          <S.ModalText>
            Deseja cancelar a solicitação de prorrogação?
          </S.ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => setConfirmClose(false)}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={() => {
                setOpenOtherExtensionToFill(true);
                setExtensionReasonList([]);
                setDataToExtend({});
                closeModal();
              }}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}
    </Modal>
  );
};
